import React, { memo, useEffect } from 'react'
import { Form, Checkbox } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { KeywordCheckbox } from './keyword-checkbox'
import { sortKeywordsForDisplay } from '../helpers'

export interface KeywordsSectionProps {
    readonly form: FormInstance
    readonly keywords: string[]
    readonly selectedKeywords: string[] | null
    readonly onKeywordChange: (keyword: string, checked: boolean) => void
    readonly onMainCheckboxChange: (event: { target: { checked: boolean } }) => void
    readonly title: React.ReactNode
    readonly className?: string
    readonly allSelected: boolean
    readonly partialSelected: boolean
}

/**
 * KeywordsSection component
 * Reusable keywords selection component with grid layout
 * Used by both AI results and page analyzer results
 */
export const KeywordsSection = memo(
    ({
        form,
        keywords,
        selectedKeywords,
        onKeywordChange,
        onMainCheckboxChange,
        title,
        className = '',
        allSelected,
        partialSelected,
    }: KeywordsSectionProps) => {
        // Sort keywords for display using column-major layout
        const sortedKeywords = sortKeywordsForDisplay(keywords)

        // Initialize form field value based on allSelected prop
        useEffect(() => {
            form.setFieldsValue({ keywords: allSelected })
        }, [form, allSelected])

        return (
            <div className={`keywords-section ${className}`.trim()}>
                <Form form={form} size="small">
                    <div className="field-section">
                        <Form.Item name="keywords" valuePropName="checked">
                            <Checkbox
                                indeterminate={partialSelected}
                                checked={allSelected}
                                onChange={onMainCheckboxChange}
                            >
                                {title}
                            </Checkbox>
                        </Form.Item>
                        <div className="field-preview keywords-preview">
                            <div className="keywords-grid">
                                {sortedKeywords.map((kw: string) => (
                                    <KeywordCheckbox
                                        key={kw}
                                        value={kw}
                                        checked={selectedKeywords?.includes(kw) ?? false}
                                        onChange={(checked) => onKeywordChange(kw, checked)}
                                        className="keyword-checkbox"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        )
    },
)

KeywordsSection.displayName = 'KeywordsSection'
